/**
 * <pw-bag-tag-builder>: the Build Your Own Bag Tag customer experience.
 *
 * A Bag Tag is a run of letter and symbol charms on a paracord (not a clicker). Customers add
 * letters by typing and symbols by picking them, then reorder, remove and customise each charm
 * independently through the option groups the catalogue declares. The build is previewed and priced
 * in integer cents (charms × R5 + R15 paracord) and added as one configured line plus the two priced
 * charge lines in a single verified request. All rules come from assets/pw-bag-tag-schema.js; no
 * symbol or colour is invented here.
 */

import { buildCartItems, findEntry, formatZar, newConfigId } from '@theme/pw-config-core';
import * as schema from '@theme/pw-bag-tag-schema';
import { addBuildToCart } from '@theme/pw-config-cart';
import { announce, createStepFlow, el, readJsonBlock, renderRadioGroup, replaceChildren, setStepDone, swatchStyle } from '@theme/pw-config-ui';

class PwBagTagBuilder extends HTMLElement {
  #config = null;
  #catalogue = null;
  #catalogueProblems = [];
  #charms = [];
  #activeIndex = 0;
  /** Assembly choice id (ASSEMBLY_OPTIONS); null until the customer picks one — Add to cart stays disabled. */
  #assembly = null;
  #busy = false;
  #refs = {};
  /** Progressive steps: charms → customise → assembly (pw-config-ui createStepFlow). */
  #flow = null;

  connectedCallback() {
    this.#config = readJsonBlock(this, 'script[data-pwc-config]', { errors: ['Builder configuration is missing.'] });
    this.#catalogue = readJsonBlock(this, 'script[data-pwc-catalogue]', null);
    this.#catalogueProblems = schema.validateCatalogue(this.#catalogue);
    for (const node of this.querySelectorAll('[data-pwc]')) this.#refs[node.dataset.pwc] = node;
    this.#flow = createStepFlow(
      this,
      [
        { id: 'charms', valid: () => this.#charms.length > 0, summary: () => `${this.#glyphText()} · ${this.#countText()}` },
        { id: 'customise', valid: () => this.#charms.length > 0 && this.#charmProblems.length === 0, summary: () => this.#designSummary() },
        { id: 'assembly', valid: () => Boolean(schema.ASSEMBLY_OPTIONS[this.#assembly]), summary: () => this.#assemblyLabel },
      ],
      { onEdit: () => this.#afterNavigation() }
    );

    this.#verifyConfig();
    this.#bind();
    this.#renderSymbols();
    this.#render();
  }

  /* ---------------------------------------------------------------- progressive steps */

  #continue(stepId) {
    if (!this.#flow.advance(stepId)) {
      announce(this, stepId === 'charms' ? 'Add at least one charm first.' : this.#priceBlocker || 'Complete this step first.');
      return;
    }
    this.#afterNavigation();
  }

  #completeCardStep(stepId) {
    this.#flow.advance(stepId);
    this.#afterNavigation();
  }

  #afterNavigation() {
    this.#render();
    this.#flow.focusCurrent();
    announce(this, `Step: ${this.querySelector(`[data-pwc-step="${this.#flow.current}"] .pwc-step__heading`)?.textContent || this.#flow.current}.`);
  }

  #glyphText() {
    return this.#charms.map((charm) => schema.charmGlyph(charm, this.#catalogue)).join(' ');
  }

  #countText() {
    const count = this.#charms.length;
    return `${count} ${count === 1 ? 'charm' : 'charms'}`;
  }

  /** "4 charms · White base / Black text" when every charm shares one design, else "4 charms · mixed designs". */
  #designSummary() {
    const group = this.#previewGroup();
    const labels = new Set(this.#charms.map((charm) => (group ? findEntry(group.entries, charm.options?.[group.id])?.label : null) || ''));
    const single = labels.size === 1 ? [...labels][0] : '';
    return `${this.#countText()} · ${single || (group ? 'mixed designs' : 'customised')}`;
  }

  /* ---------------------------------------------------------------- configuration integrity */

  get #configErrors() {
    return Array.isArray(this.#config?.errors) ? this.#config.errors : [];
  }

  #verifyConfig() {
    const errors = this.#configErrors;
    const charges = this.#config?.charges || {};
    if (!errors.length) {
      if (Number(this.#config?.configured?.priceCents) !== schema.CONFIGURED_PRICE_CENTS) errors.push('Configured product price is not R0.');
      if (Number(charges.charm?.priceCents) !== schema.CHARM_PRICE_CENTS) errors.push('Charm charge price is not R5.');
      if (Number(charges.paracord?.priceCents) !== schema.PARACORD_PRICE_CENTS) errors.push('Paracord charge price is not R15.');
    }
    if (this.#catalogueProblems.length) errors.push(`Catalogue is invalid: ${this.#catalogueProblems[0]}`);
    this.#config = { ...(this.#config || {}), errors };
  }

  get #variants() {
    return {
      configuredVariantId: Number(this.#config?.configured?.variantId),
      charmChargeVariantId: Number(this.#config?.charges?.charm?.variantId),
      paracordChargeVariantId: Number(this.#config?.charges?.paracord?.variantId),
    };
  }

  /* ------------------------------------------------------------------------------- events */

  #bind() {
    const { 'letter-input': input, form, assembly } = this.#refs;
    input?.addEventListener('input', () => this.#onLetterInput());
    input?.addEventListener('keydown', (event) => {
      if (event.key === 'Backspace' && !input.value && this.#charms.length) {
        event.preventDefault();
        this.#activeIndex = this.#charms.length - 1;
        this.#removeActive();
      }
      if (event.key === 'Enter') {
        event.preventDefault();
        this.#continue('charms');
      }
    });
    for (const button of this.querySelectorAll('[data-pwc-continue]')) {
      button.addEventListener('click', () => this.#continue(button.dataset.pwcContinue));
    }
    form?.addEventListener('submit', (event) => {
      event.preventDefault();
      this.#addToCart();
    });
    this.#refs['move-left']?.addEventListener('click', () => this.#move(-1));
    this.#refs['move-right']?.addEventListener('click', () => this.#move(1));
    this.#refs['remove-charm']?.addEventListener('click', () => this.#removeActive());
    // The card step completes on the pick: `change` for a new value, `click` so re-picking the same card after an Edit also moves on.
    const onAssembly = (event) => {
      const value = event.target?.value;
      if (!event.target?.matches?.('input[type="radio"]') || !schema.ASSEMBLY_OPTIONS[value]) return;
      this.#assembly = value;
      setStepDone(this, 'assembly', true);
      this.#completeCardStep('assembly');
    };
    assembly?.addEventListener('change', onAssembly);
    assembly?.addEventListener('click', onAssembly);
  }

  /**
   * The "Add letters" field always shows the build's letters ("JESS"), like the Name Clicker's name field, and
   * every edit to it (typing, deleting, pasting, editing in the middle) is reconciled against the charms so that
   * untouched charms keep their position and colour design. Until 2026-10-17 the field was emptied after each
   * keystroke (letters were only visible as chips further down), which read as "the letters are not showing".
   */
  #onLetterInput() {
    const input = this.#refs['letter-input'];
    const before = this.#charms;
    const { charms, dropped } = schema.reconcileLetters(before, input.value, this.#catalogue);
    this.#refs['letter-hint'].textContent = dropped ? 'Only the available letters can be added. Pick symbols from the list below.' : '';
    this.#refs['letter-hint'].classList.toggle('is-error', dropped);
    const delta = charms.length - before.length;
    this.#charms = charms;
    if (delta > 0) {
      // Select the last charm that was just added (the first position where the lists differ, plus the additions).
      let changedAt = 0;
      while (changedAt < before.length && changedAt < charms.length && before[changedAt].kind === charms[changedAt].kind && before[changedAt].value === charms[changedAt].value) changedAt += 1;
      this.#activeIndex = Math.min(charms.length - 1, changedAt + delta - 1);
      announce(this, `Added ${delta} ${delta === 1 ? 'charm' : 'charms'}. ${charms.length} in total.`);
    } else if (delta < 0) {
      this.#activeIndex = Math.max(0, Math.min(this.#activeIndex, charms.length - 1));
      announce(this, `Removed ${-delta} ${delta === -1 ? 'charm' : 'charms'}. ${charms.length} ${charms.length === 1 ? 'charm' : 'charms'} left.`);
    }
    this.#render();
  }

  /** Keeps the letters field equal to the build's letters (normalised: upper case, unsupported characters gone). */
  #syncLetterInput() {
    const input = this.#refs['letter-input'];
    if (!input) return;
    const letters = schema.lettersOf(this.#charms);
    if (input.value !== letters) input.value = letters;
  }

  #addSymbol(symbolId) {
    try {
      this.#charms = [...this.#charms, schema.createSymbolCharm(symbolId, this.#catalogue, this.#charms[this.#charms.length - 1])];
    } catch (_) {
      return;
    }
    this.#activeIndex = this.#charms.length - 1;
    announce(this, `Added ${findEntry(this.#catalogue.symbols, symbolId)?.label || 'symbol'}. ${this.#charms.length} charms in total.`);
    this.#render();
  }

  #selectCharm(index) {
    this.#activeIndex = index;
    this.#render();
    this.#refs['charm-editor']?.focus();
  }

  #move(delta) {
    const to = this.#activeIndex + delta;
    if (to < 0 || to >= this.#charms.length) return;
    this.#charms = schema.moveCharm(this.#charms, this.#activeIndex, to);
    this.#activeIndex = to;
    announce(this, `Charm moved to position ${to + 1}.`);
    this.#render();
  }

  #removeActive() {
    if (!this.#charms.length) return;
    const removed = this.#charms[this.#activeIndex];
    this.#charms = schema.removeCharm(this.#charms, this.#activeIndex);
    this.#activeIndex = Math.max(0, Math.min(this.#activeIndex, this.#charms.length - 1));
    announce(this, `Removed ${schema.charmGlyph(removed, this.#catalogue)}. ${this.#charms.length} ${this.#charms.length === 1 ? 'charm' : 'charms'} left.`);
    this.#render();
    this.#refs['letter-input']?.focus();
  }

  #setOption(groupId, entryId, applyToAll = false) {
    this.#charms = this.#charms.map((charm, index) =>
      applyToAll || index === this.#activeIndex ? { ...charm, options: { ...charm.options, [groupId]: entryId } } : charm
    );
    this.#render();
  }

  /* ----------------------------------------------------------------------------- derived */

  get #charmProblems() {
    const problems = [];
    this.#charms.forEach((charm, index) => {
      for (const group of this.#catalogue?.options || []) {
        if (!findEntry(group.entries, charm.options?.[group.id])) problems.push({ index, group });
      }
    });
    return problems;
  }

  /** Why the build cannot be priced yet (store setup, catalogue, charms), or '' when the total is live. */
  get #priceBlocker() {
    if (this.#configErrors.length) return 'This builder is not configured yet. Please check back soon.';
    if (!schema.catalogueIsComplete(this.#catalogue)) return 'Charm colour designs are coming soon.';
    if (!this.#charms.length) return 'Add at least one charm to start your build.';
    const problem = this.#charmProblems[0];
    if (problem) return `Choose a ${problem.group.label.toLowerCase()} for charm ${problem.index + 1}.`;
    return '';
  }

  /** Why Add to cart is disabled: a pricing problem, else the assembly choice the customer still has to make. */
  get #blocker() {
    return this.#priceBlocker || (schema.ASSEMBLY_OPTIONS[this.#assembly] ? '' : 'Choose Self assembly or Assemble for me.');
  }

  get #assemblyLabel() {
    return schema.ASSEMBLY_OPTIONS[this.#assembly]?.label || '';
  }

  get #pricing() {
    return schema.priceConfiguration(this.#charms.length);
  }

  #previewGroup() {
    return schema.previewGroup(this.#catalogue);
  }

  /* ---------------------------------------------------------------------------- rendering */

  #render() {
    this.#renderErrors();
    this.#syncLetterInput();
    this.#renderPreview();
    this.#renderCharms();
    this.#renderEditor();
    this.#renderSummary();
    setStepDone(this, 'charms', this.#charms.length > 0);
    setStepDone(this, 'customise', this.#charms.length > 0 && this.#charmProblems.length === 0);
    const continueCharms = this.querySelector('[data-pwc-continue="charms"]');
    if (continueCharms) continueCharms.disabled = this.#charms.length === 0;
    const continueCustomise = this.querySelector('[data-pwc-continue="customise"]');
    if (continueCustomise) continueCustomise.disabled = !(this.#charms.length > 0 && this.#charmProblems.length === 0);
    this.#flow?.render();
  }

  #renderErrors() {
    const box = this.#refs['config-error'];
    if (!box) return;
    const errors = this.#configErrors;
    box.hidden = errors.length === 0;
    replaceChildren(
      box,
      el('p', { className: 'pwc-alert__title', text: 'This builder is not available yet.' }),
      el('p', { className: 'pwc-alert__text', text: 'Add to cart is disabled until the store setup is complete.' }),
      errors.length ? el('ul', { className: 'pwc-alert__list' }, errors.map((error) => el('li', { text: error }))) : null
    );
    const empty = this.#refs['catalogue-empty'];
    if (empty) empty.hidden = errors.length > 0 || schema.catalogueIsComplete(this.#catalogue);
  }

  #renderSymbols() {
    const container = this.#refs.symbols;
    if (!container) return;
    const symbols = this.#catalogue?.symbols || [];
    const group = container.closest('.pwc-group') || container;
    if (!symbols.length) {
      // Symbols are optional (the confirmed catalogue is letters + colour designs): with none, hide the picker
      // entirely rather than promise charms that are not coming.
      group.hidden = true;
      replaceChildren(container);
      return;
    }
    group.hidden = false;
    replaceChildren(
      container,
      el(
        'div',
        { className: 'pwc-tiles pwc-tiles--symbols', role: 'group', 'aria-label': 'Add a symbol charm' },
        symbols.map((symbol) =>
          el(
            'button',
            {
              type: 'button',
              className: 'pwc-tile pwc-tile--symbol',
              'aria-label': `Add ${symbol.label}`,
              onClick: () => this.#addSymbol(symbol.id),
            },
            [el('span', { className: 'pwc-tile__glyph', 'aria-hidden': 'true', text: symbol.glyph || symbol.label }), el('span', { className: 'pwc-tile__label', text: symbol.label })]
          )
        )
      )
    );
  }

  #renderPreview() {
    const list = this.#refs.preview;
    if (!list) return;
    if (!this.#charms.length) {
      replaceChildren(list, el('li', { className: 'pwc-preview__empty', text: 'Your charms will appear here in order, on the paracord.' }));
    } else {
      const previewGroup = this.#previewGroup();
      replaceChildren(
        list,
        ...this.#charms.map((charm, index) => {
          const entry = previewGroup ? findEntry(previewGroup.entries, charm.options?.[previewGroup.id]) : null;
          // The selected design controls BOTH colours: base via --pwc-swatch, text via --pwc-text (swatchStyle)
          return el(
            'li',
            {
              className: `pwc-charm-tile${index === this.#activeIndex ? ' is-active' : ''}${charm.kind === 'symbol' ? ' pwc-charm-tile--symbol' : ''}${entry?.swatch && entry?.text ? ' pwc-charm-tile--design' : ''}`,
              style: swatchStyle(entry),
              dataset: entry ? { design: entry.id } : undefined,
              title: charm.kind === 'letter' ? `Letter ${charm.value}` : findEntry(this.#catalogue?.symbols, charm.value)?.label || charm.value,
            },
            [el('span', { className: 'pwc-charm-tile__glyph', text: schema.charmGlyph(charm, this.#catalogue) })]
          );
        })
      );
    }
    const attachment = this.#refs['preview-attachment'];
    if (attachment) attachment.textContent = this.#charms.length ? schema.ATTACHMENTS.paracord.label : '';
  }

  #renderCharms() {
    const list = this.#refs.charms;
    if (!list) return;
    const problems = this.#charmProblems;
    replaceChildren(
      list,
      ...this.#charms.map((charm, index) => {
        const incomplete = problems.some((problem) => problem.index === index);
        const glyph = schema.charmGlyph(charm, this.#catalogue);
        const name = charm.kind === 'letter' ? `letter ${charm.value}` : findEntry(this.#catalogue?.symbols, charm.value)?.label || charm.value;
        return el('li', { className: 'pwc-chip-item' }, [
          el(
            'button',
            {
              type: 'button',
              className: `pwc-chip${index === this.#activeIndex ? ' is-active' : ''}${incomplete ? ' is-incomplete' : ''}`,
              'aria-current': index === this.#activeIndex ? 'true' : undefined,
              'aria-label': `Charm ${index + 1}, ${name}${incomplete ? ', needs options' : ''}`,
              onClick: () => this.#selectCharm(index),
            },
            [el('span', { className: 'pwc-chip__num', text: String(index + 1) }), el('span', { className: 'pwc-chip__char', text: glyph })]
          ),
        ]);
      })
    );
    const hint = this.#refs['charms-hint'];
    if (hint) hint.hidden = this.#charms.length > 0;
  }

  #renderEditor() {
    const editor = this.#refs['charm-editor'];
    if (!editor) return;
    const charm = this.#charms[this.#activeIndex];
    editor.hidden = !charm;
    if (!charm) return;
    const total = this.#charms.length;
    const name = charm.kind === 'letter' ? `Letter ${charm.value}` : findEntry(this.#catalogue?.symbols, charm.value)?.label || charm.value;
    this.#refs['editor-title'].textContent = `Charm ${this.#activeIndex + 1} of ${total}: ${name}`;
    this.#refs['move-left'].disabled = this.#activeIndex === 0;
    this.#refs['move-right'].disabled = this.#activeIndex === total - 1;

    const groups = (this.#catalogue?.options || []).map((group) =>
      renderRadioGroup({
        id: `pwc-bt-${group.id}`,
        label: group.label,
        entries: group.entries,
        selectedId: charm.options?.[group.id] ?? null,
        assetBase: this.#config?.assetBase,
        onSelect: (id) => this.#setOption(group.id, id),
        action: total > 1 ? { label: 'Use for all charms', onClick: () => this.#setOption(group.id, charm.options?.[group.id], true) } : undefined,
      })
    );
    replaceChildren(
      this.#refs.groups,
      ...(groups.length ? groups : [el('p', { className: 'pwc-group__empty', text: 'Per-charm colour designs are coming soon.' })])
    );
  }

  #renderSummary() {
    const { charmCents, attachmentCents, totalCents } = this.#pricing;
    const count = this.#charms.length;
    const summary = this.#refs.summary;
    if (summary) {
      replaceChildren(
        summary,
        el('div', { className: 'pwc-line' }, [
          el('dt', { text: count ? `${count} ${count === 1 ? 'charm' : 'charms'} × ${formatZar(schema.CHARM_PRICE_CENTS)}` : 'Charms' }),
          el('dd', { text: count ? formatZar(charmCents) : '—' }),
        ]),
        el('div', { className: 'pwc-line' }, [el('dt', { text: schema.ATTACHMENTS.paracord.label }), el('dd', { text: formatZar(attachmentCents) })]),
        el('div', { className: `pwc-line${this.#assemblyLabel ? '' : ' is-incomplete'}` }, [
          el('dt', { text: this.#assemblyLabel || 'Assembly' }),
          el('dd', { text: this.#assemblyLabel ? 'No charge' : 'Choose in step 3' }),
        ])
      );
    }
    // Never present R0 as the build's price: until the configuration is valid, show the prompt
    // (section setting); as soon as it is valid, show the live total, updated on every change.
    if (this.#refs.total) {
      const valid = !this.#priceBlocker;
      this.#refs.total.textContent = valid ? formatZar(totalCents) : this.#refs.total.dataset.prompt || 'Build your price';
      this.#refs.total.classList.toggle('is-prompt', !valid);
    }
    const text = this.#refs['summary-text'];
    if (text) {
      text.textContent = count
        ? `${this.#charms.map((charm) => schema.charmGlyph(charm, this.#catalogue)).join(' ')} · ${count} ${count === 1 ? 'charm' : 'charms'} · Paracord${this.#assemblyLabel ? ` · ${this.#assemblyLabel}` : ''}`
        : 'Add charms to start your build.';
      text.classList.toggle('is-incomplete', !count);
    }
    this.#renderAtc();
  }

  #renderAtc() {
    const button = this.#refs.atc;
    const label = this.#refs['atc-text'];
    if (!button || !label || this.#busy) return;
    const blocker = this.#blocker;
    button.disabled = Boolean(blocker);
    label.textContent = blocker || `Add to cart — ${formatZar(this.#pricing.totalCents)}`;
    button.classList.toggle('is-ready', !blocker);
  }

  /* ---------------------------------------------------------------------------- add to cart */

  async #addToCart() {
    if (this.#busy || this.#blocker) return;
    const button = this.#refs.atc;
    const label = this.#refs['atc-text'];
    const error = this.#refs.error;
    const notice = this.#refs.notice;
    error.hidden = true;
    notice.hidden = true;

    let config;
    let items;
    try {
      config = schema.createConfiguration({ configId: newConfigId(schema.ID_PREFIX), charms: this.#charms, variants: this.#variants, assemblyId: this.#assembly });
      const problems = schema.validateConfiguration(config, this.#catalogue);
      if (problems.length) throw new Error(problems[0]);
      items = buildCartItems(schema.toCartBuild(config, this.#catalogue));
    } catch (failure) {
      this.#showError(
        failure instanceof RangeError
          ? 'This bag tag has too many charms to save with an order. Please contact us and we will build it for you.'
          : `This build cannot be added: ${failure.message}`
      );
      return;
    }

    this.#busy = true;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    button.classList.add('is-loading');
    label.textContent = 'Adding…';
    announce(this, 'Adding your Bag Tag to the cart.');

    const result = await addBuildToCart({
      items,
      configId: config.configId,
      expected: { configuredVariantId: config.variants.configuredVariantId, lines: schema.expectedLines(config) },
      // Build-level analytics fact (one custom pixel event per build; see pw-config-cart publishBuildAdded)
      analytics: { schema: schema.SCHEMA_ID, productId: this.#config?.configured?.productId, variantId: config.variants.configuredVariantId, totalCents: config.charges.totalCents, currency: 'ZAR', name: this.#config?.configured?.title || 'Build Your Own Bag Tag' },
    });

    this.#busy = false;
    button.removeAttribute('aria-busy');
    button.classList.remove('is-loading');
    if (!result.ok) {
      this.#showError(result.error);
      this.#renderAtc();
      return;
    }
    notice.hidden = false;
    announce(this, `Added to cart: ${config.charmCount} charm bag tag, ${formatZar(config.charges.totalCents)}.`);
    label.textContent = 'Added to cart';
    setTimeout(() => this.#renderAtc(), 3000);
  }

  #showError(message) {
    const error = this.#refs.error;
    if (!error) return;
    error.hidden = false;
    error.textContent = message;
    announce(this, message);
  }
}

if (!customElements.get('pw-bag-tag-builder')) {
  customElements.define('pw-bag-tag-builder', PwBagTagBuilder);
}
